import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { BusinessUnitToggle } from "@/shared/types";
import { axiosApi } from "@/Utils";
import { Layer } from "@/Utils/hierarchy";

import { selectRois } from "./helpers/selectRois";
import { ChannelTotalROIMap } from "../types";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";

export interface PaidMediaNode {
  customer: string;
  channel: string;
  geography: string;
  division: string;
  brand: string;
  category: string;
  product: string;
  node_uuid: string;
  data: {
    revenue: number;
    spend: number;
    unit: number;
    sampleSize: number;
    adstockPeak: number;
    adstockDuration: number;
  };
}

export interface ROIData {
  spend: number;
  revenue: number;
  adstockPeak: number;
  adstockDuration: number;
}

export interface ROIDto {
  channel: string;
  geography: string;
  data: ROIData;
  layer?: Layer;
}

export type RoiData = {
  nodeUuid: string;
  aggregated: ChannelTotalROIMap;
  split: ChannelTotalROIMap;
  totals: { totalSpend: number; totalRevenue: number };
};

const getRois = ({
  startDate,
  endDate,
  brand,
  category,
  product,
  channels,
  funnelStage,
  geographies,
  businessUnitToggle,
  nodeUuids,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand: string;
  category: string;
  product: string;
  aggregateGeography?: boolean;
  channels?: string[];
  funnelStage?: string;
  geographies?: string[];
  businessUnitToggle?: BusinessUnitToggle;
  nodeUuids?: string[];
}): Promise<RoiData[]> => {
  const params = new URLSearchParams();

  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (brand) params.append("brand", brand);
  if (category) params.append("category", category);
  if (product) params.append("product", product);
  if (businessUnitToggle) params.append("businessUnitToggle", businessUnitToggle);

  // If funnelStage is 'all', append brand, category, and product
  if (funnelStage === "all") {
    if (brand) params.append("funnelStages", "brand");
    if (category) params.append("funnelStages", "category");
    if (product) params.append("funnelStages", "product");
  } else if (funnelStage) {
    params.append("funnelStages", funnelStage);
  }

  if (channels && channels.length > 0) {
    channels.forEach((channel) => {
      params.append("channels", channel);
    });
  }

  if (geographies && geographies.length > 0) {
    geographies.forEach((geography) => {
      params.append("geographies", geography);
    });
  }

  if (nodeUuids && nodeUuids.length > 0) {
    nodeUuids.forEach((nodeUuid) => {
      params.append("nodeUuids", nodeUuid);
    });
  }

  return axiosApi.get(`/api/roi?${params.toString()}`, {
    baseURL: NEST_BACKEND_URL,
  });
};

const getRoisOld = ({
  startDate,
  endDate,
  brand,
  category,
  product,
  shouldShiftSpend,
  channels,
  funnelStage,
  geographies,
  businessUnitToggle,
  nodeUuids,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand: string;
  category: string;
  product: string;
  aggregateGeography?: boolean;
  shouldShiftSpend?: boolean;
  channels?: string[];
  funnelStage?: string;
  geographies?: string[];
  businessUnitToggle?: BusinessUnitToggle;
  nodeUuids?: string[];
}): Promise<RoiData[]> => {
  const payload: Record<string, any> = {
    startDate,
    endDate,
    brand,
    category,
    product,
    shouldShiftSpend,
    channels,
    funnelStage,
    geographies,
    businessUnitToggle,
    nodeUuids,
  };

  return axiosApi.get("/api/v2/roi/v2", {
    params: payload,
    baseURL: INSIGHTS_BACKEND_URL,
  });
};

export const useRois = ({
  startDate,
  endDate,
  brand,
  category,
  product,
  config,
  aggregateGeography,
  shouldShiftSpend,
  channels,
  funnelStage,
  geographies,
  businessUnitToggle,
  nodeUuids,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand: string;
  category: string;
  product: string;
  aggregateGeography?: boolean;
  shouldShiftSpend?: boolean;
  channels?: string[];
  funnelStage?: string;
  geographies?: string[];
  businessUnitToggle?: BusinessUnitToggle;
  nodeUuids?: string[];
  config?: QueryConfig<typeof getRois>;
}) => {
  const endpointType = useModuleEndpoint("channel-roi");

  const queryKey = useMemo(
    () => [
      "roi",
      startDate,
      endDate,
      brand,
      category,
      product,
      aggregateGeography,
      shouldShiftSpend,
      channels,
      funnelStage,
      geographies,
      businessUnitToggle,
      nodeUuids,
    ],
    [
      startDate,
      endDate,
      brand,
      category,
      product,
      aggregateGeography,
      shouldShiftSpend,
      channels,
      funnelStage,
      geographies,
      businessUnitToggle,
      nodeUuids,
    ]
  );

  const queryFn = useCallback(() => {
    if (endpointType === "new") {
      return getRois({
        startDate,
        endDate,
        brand,
        category,
        product,
        aggregateGeography,
        channels,
        funnelStage,
        geographies,
        businessUnitToggle,
        nodeUuids,
      });
    } else {
      return getRoisOld({
        startDate,
        endDate,
        brand,
        category,
        product,
        aggregateGeography,
        shouldShiftSpend,
        channels,
        funnelStage,
        geographies,
        businessUnitToggle,
        nodeUuids,
      });
    }
  }, [
    startDate,
    endDate,
    brand,
    category,
    product,
    aggregateGeography,
    channels,
    funnelStage,
    geographies,
    businessUnitToggle,
    nodeUuids,
  ]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
    select: selectRois,
  });
};
